import { getProductsVariantService } from "../services/productService";
import {
  getVendorsService,
  getVendorByIdService,
  getVendorProductsService,
} from "../services/vendorService";

let cachedVendors = null;
let cachedVendorMap = new Map();

const toSlug = (value = "") =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const normaliseLegacyVendorRecord = (item = {}) => {
  const slugSource =
    item.containerPath ||
    item.codice ||
    item.denominazioneSociale ||
    item.nome ||
    item._id ||
    "";

  const slug = toSlug(slugSource) || item._id || "";

  const image =
    typeof item.immagine === "string"
      ? item.immagine
      : item.immagine?.path || "";

  return {
    id: item._id || slug || item.codice || item.nome || "",
    _id: item._id || slug || item.codice || item.nome || "",
    containerPath: slug,
    slug,
    name: item.denominazioneSociale || item.nome || item.codice || "",
    description: item.descrizione || item.bio || item.description || "",
    image,
    contatti: ensureArray(item.contatti),
    products: ensureArray(item.products),
    totalProducts: item.totArticles ?? item.totalProducts ?? 0,
    gallery: ensureArray(item.gallery),
    tags: ensureArray(item.tags),
    raw: item,
  };
};

const buildVendorsFromVariants = (variants = []) => {
  const vendorMap = new Map();

  variants.forEach((variant) => {
    const vendorId =
      variant._c ||
      variant._venditore?._id ||
      variant._venditore ||
      variant._prodotto?.[0]?._id ||
      variant._id;

    if (!vendorId) {
      return;
    }

    const existing =
      vendorMap.get(vendorId) ||
      {
        id: vendorId,
        name: "",
        description: "",
        image: "",
        tags: new Set(),
        products: [],
        raw: [],
      };

    existing.products.push(variant);
    existing.raw.push(variant);

    const candidateName =
      variant.brand ||
      variant._prodotto?.[0]?.tag ||
      variant._prodotto?.[0]?.nome ||
      variant.codice;
    if (!existing.name && candidateName) {
      existing.name = candidateName;
    }

    const candidateDescription =
      variant.descrizione || variant._prodotto?.[0]?.tag || "";
    if (!existing.description && candidateDescription) {
      existing.description = candidateDescription;
    }

    const imagePath = variant.immagine?.[0]?.path;
    if (!existing.image && imagePath) {
      existing.image = imagePath;
    }

    [
      variant.brand,
      variant.colore,
      variant.taglia,
      variant._prodotto?.[0]?.tag,
    ]
      .filter(Boolean)
      .forEach((tag) => existing.tags.add(tag));

    vendorMap.set(vendorId, existing);
  });

  const slugCounts = new Map();

  return Array.from(vendorMap.values()).map((vendor) => {
    const baseSlugSource = vendor.name || vendor.id;
    let slug = toSlug(baseSlugSource) || toSlug(vendor.id) || vendor.id;

    if (slugCounts.has(slug)) {
      const count = slugCounts.get(slug) + 1;
      slugCounts.set(slug, count);
      slug = `${slug}-${count}`;
    } else {
      slugCounts.set(slug, 1);
    }

    return {
      id: vendor.id,
      _id: vendor.id,
      containerPath: slug,
      slug,
      name: vendor.name || `Vendor ${slugCounts.get(slug)}`,
      description: vendor.description || "",
      image: vendor.image || "",
      tags: Array.from(vendor.tags).filter(Boolean),
      products: vendor.products,
      totalProducts: vendor.products.length,
      gallery: vendor.products
        .map((product) => product.immagine?.[0]?.path)
        .filter(Boolean)
        .slice(0, 6),
      raw: vendor.raw,
    };
  });
};

const normaliseVendorResponse = (response = {}) => {
  if (Array.isArray(response.varianti) && response.varianti.length) {
    return buildVendorsFromVariants(response.varianti);
  }

  const vendorItems = response.venditori ?? response.categorie ?? [];
  return vendorItems.map(normaliseLegacyVendorRecord);
};

const addVendorToCache = (vendor, mutateArray = true) => {
  if (!vendor) return;

  if (!cachedVendors) {
    cachedVendors = [];
  }

  const existingIndex = cachedVendors.findIndex((item) => item.id === vendor.id);
  if (existingIndex === -1) {
    if (mutateArray) {
      cachedVendors.push(vendor);
    }
  } else if (mutateArray) {
    cachedVendors.splice(existingIndex, 1, vendor);
  }

  const keys = [
    vendor.id,
    vendor._id,
    vendor.slug,
    vendor.containerPath,
    vendor.name,
    vendor.raw?.codice,
  ].filter(Boolean);

  keys.forEach((key) => {
    cachedVendorMap.set(key, vendor);
    const slugKey = toSlug(key);
    if (slugKey) {
      cachedVendorMap.set(slugKey, vendor);
    }
  });
};

const cacheVendors = (vendors = []) => {
  cachedVendors = vendors;
  cachedVendorMap = new Map();
  vendors.forEach((vendor) => addVendorToCache(vendor, false));
};

export async function fetchVendors(force = false) {
  if (!force && cachedVendors) {
    return cachedVendors;
  }

  try {
    const response = await getVendorsService();
    const vendors = normaliseVendorResponse(response);
    cacheVendors(vendors);
    return vendors;
  } catch (error) {
    console.error("Failed to fetch vendors:", error);
    return [];
  }
}

export async function findVendor(identifier) {
  if (!identifier) {
    return null;
  }

  const slug = toSlug(identifier);

  if (!cachedVendors) {
    await fetchVendors();
  }

  let vendor =
    cachedVendorMap.get(identifier) || cachedVendorMap.get(slug) || null;

  if (vendor) {
    return vendor;
  }

  const isLikelyObjectId = /^[a-f0-9]{24}$/i.test(identifier);
  if (!isLikelyObjectId) {
    return null;
  }

  try {
    const remoteVendor = await getVendorByIdService(identifier);
    if (!remoteVendor) {
      return null;
    }
    const normalised = normaliseLegacyVendorRecord(remoteVendor);
    addVendorToCache(normalised);
    return normalised;
  } catch (error) {
    console.error("Failed to fetch vendor detail:", error);
    return null;
  }
}

const normaliseVendorProduct = (item = {}) => {
  const images = ensureArray(item.immagine).map((image) =>
    typeof image === "string" ? { path: image } : image
  );
  const prezzo = item.prezzo ?? 0;

  return {
    _id: item._id,
    prezzo,
    descrizione: item.descrizione || "",
    immagine: images,
    _astaCorrente: [],
    _prodotto: [
      {
        _id: item._id,
        nome: item.nome || "",
        tag: item.tag || item.descrizione || "",
      },
    ],
    brand: item.brand || "",
    numeroVarianti: item.numeroVarianti,
    _venditore: ensureArray(item._venditore),
    type: "single",
  };
};

export async function fetchVendorProducts(
  vendorIdentifier,
  { limit = 10, page = 1 } = {}
) {
  const vendor = await findVendor(vendorIdentifier);

  const vendorId = vendor?._id || vendor?.id || vendorIdentifier;

  if (!vendorId) {
    return [];
  }

  if (page === 1 && Array.isArray(vendor?.products) && vendor.products.length) {
    return vendor.products;
  }

  try {
    const response = await getVendorProductsService({
      vendorId,
      limit: limit.toString(),
      page: page.toString(),
    });

    const products = ensureArray(response?.prodotti);

    const variantsNested = await Promise.all(
      products.map(async (product) => {
        try {
          const variantResponse = await getProductsVariantService({
            prodottoId: product._id,
          });
          const variants = ensureArray(variantResponse?.varianti);
          if (variants.length) {
            return variants;
          }
          return [normaliseVendorProduct(product)];
        } catch (error) {
          console.error("Failed to fetch product variants:", error);
          return [normaliseVendorProduct(product)];
        }
      })
    );

    const flattened = variantsNested.flat().filter(Boolean);

    if (vendor && page === 1) {
      vendor.products = flattened;
      const totalFromResponse = response?.totElements ?? flattened.length;
      const parsedTotal = parseInt(totalFromResponse, 10);
      vendor.totalProducts = Number.isFinite(parsedTotal)
        ? parsedTotal
        : flattened.length;
      addVendorToCache(vendor);
    }

    return flattened;
  } catch (error) {
    console.error("Failed to fetch vendor products:", error);
    return [];
  }
}
